import { useCallback, useEffect, useState } from 'react';
import * as Popover from '@radix-ui/react-dropdown-menu';
import { Bell } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { timeAgo } from '../lib/format';
import type { FantasyAlert } from '../lib/types';

/**
 * The bell: every alert, whatever the email settings -- Bet Watch (model
 * flips, line moves) and fantasy (lineup changes, injuries, weather) -- with a
 * count. Opening it marks them read; each one links to its bet or league.
 */
export function AlertBell() {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState<FantasyAlert[]>([]);
  const [unread, setUnread] = useState(0);

  const load = useCallback(() => {
    api.fantasy.alerts().then((r) => {
      setAlerts(r.alerts.slice(0, 12));
      setUnread(r.unread);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <Popover.Root onOpenChange={(open) => { if (!open && unread) void api.fantasy.readAlerts().then(load); }}>
      <Popover.Trigger asChild>
        <button className="btn btn-ghost btn-sm icon-btn bell" aria-label={unread ? `${unread} unread alerts` : 'Alerts'} title="Alerts">
          <Bell size={16} strokeWidth={1.75} />
          {unread > 0 && <span className="bell-count num">{unread > 9 ? '9+' : unread}</span>}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content className="ui-menu bell-menu" align="end" sideOffset={6}>
          <Popover.Label className="ui-menu-label">Alerts</Popover.Label>
          {alerts.length === 0 && <div className="bell-empty">Nothing yet. Model flips and line moves on your bets, and start/sit, injury and weather alerts for your leagues, show up here.</div>}
          {alerts.map((a) => (
            <Popover.Item
              key={a.id}
              className={`ui-menu-item bell-item ${a.read_at ? '' : 'unread'}`}
              onSelect={() => navigate(a.type.startsWith('bet_') ? '/ledger' : a.league_id ? `/fantasy?league=${a.league_id}` : '/fantasy')}
            >
              <div style={{ minWidth: 0 }}>
                <div className="bell-headline">{a.headline}</div>
                {a.detail && <div className="bell-detail">{a.detail}</div>}
                <div className="bell-time">{timeAgo(a.created_at)}</div>
              </div>
            </Popover.Item>
          ))}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
